import { ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Form, type FormInstance, Skeleton, Typography } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import type { State as RouterState } from "router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import * as commonColumns from "../../common/columns.tsx";
import { Card } from "../../common/components/Card.tsx";
import { DrawerItemWrapper } from "../../common/components/DrawerItemWrapper.tsx";
import { ListActionBar } from "../../common/components/ListActionBar.tsx";
import { NoData } from "../../common/components/NoDataComp.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import {
	TableWrapper,
	type PaginationAndFilter,
	type TableWrapperProps,
} from "../../common/components/Table.tsx";
import { turbineFilter } from "../common/filter.ts";
import { sanitizedFilter } from "../../common/functions.ts";
import { routeNames } from "../common/routes.ts";
import {
	turbineActions,
	type SideloadedTurbineInfo,
} from "../../features/turbine/turbineSlice.ts";
import type {
	Client,
	CurrentUser,
	Location,
	PaginatedturbineResultsSideloadList,
	Turbine,
} from "../generated-axios-client/index.ts";
import { ClientSelectorFormItem } from "./filter-forms/ClientSelectorFormItem.tsx";
import { LocationSelectorFormItem } from "./filter-forms/LocationSelectorFormItem.tsx";

const onFilter =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		form
			.validateFields()
			.then((values) => {
				dispatch(
					routerActions.navigateTo(
						currentRoute.name,
						sanitizedFilter({ ...currentRoute.params, ...values, page: 1 }),
					),
				);
			})
			.catch(() => {});
	};

const onCancel =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		dispatch(
			routerActions.navigateTo(
				currentRoute.name,
				sanitizedFilter({
					...currentRoute.params,
					...turbineFilter,
					page: 1,
				}),
			),
		);
		form.resetFields();
	};

type rowDataType = {
	user?: CurrentUser;
	results: PaginatedturbineResultsSideloadList;
	turbineSideload: SideloadedTurbineInfo | null;
};

const getColumns: TableWrapperProps<
	PaginatedturbineResultsSideloadList,
	AnyObject
>["getColumns"] = (intl, currentFilter) => [
	commonColumns.simpleColumn("row_number", "table_column_ticket_number", intl),
	commonColumns.simpleColumn(
		"serial_number",
		"turbine_table_column_serial_number",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"park_number",
		"turbine_table_column_park_number",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn(
		"client",
		"turbine_table_column_client",
		intl,
		currentFilter,
		"client__legal_name",
	),
	commonColumns.simpleColumn(
		"location",
		"turbine_table_column_location",
		intl,
		currentFilter,
		"location__name",
	),
	commonColumns.simpleColumn(
		"updated_at",
		"turbine_table_column_updated_at",
		intl,
		currentFilter,
	),
	commonColumns.simpleColumn("actions", "table_column_actions", intl),
];

const getTableRows: TableWrapperProps<rowDataType, AnyObject>["getRows"] = (
	intl,
	dispatch,
	data,
	modalApi,
) => {
	const { results, user, turbineSideload } = data;

	return Object.values(results.results.turbines).map(
		(turbine: Turbine, index: number) => {
			const showDeleteConfirm = () => {
				modalApi?.confirm({
					title: intl.formatMessage({ id: "turbine_delete_confirmation" }),
					icon: <ExclamationCircleFilled />,
					okText: intl.formatMessage({ id: "confirm" }),
					okType: "danger",
					cancelText: intl.formatMessage({ id: "cancel" }),
					onOk() {
						if (turbine.id) {
							dispatch(turbineActions.deleteTurbine(turbine.id));
						}
					},
					onCancel() {},
				});
			};

			const onView = () => {
				dispatch(
					routerActions.navigateTo(routeNames.TURBINE_VIEW, { id: turbine.id }),
				);
			};

			const onEdit = () => {
				dispatch(
					routerActions.navigateTo(routeNames.TURBINE_EDIT, { id: turbine.id }),
				);
			};
			const onDelete = () => {
				showDeleteConfirm();
			};

			const clientName = turbineSideload?.client?.[turbine.client]?.legal_name;
			const siteName = turbineSideload?.location?.[turbine.location]?.name;

			const onViewClient = () => {
				dispatch(
					routerActions.navigateTo(routeNames.CLIENT_VIEW, {
						id: turbine.client,
					}),
				);
			};

			const onViewSite = () => {
				dispatch(
					routerActions.navigateTo(routeNames.LOCATION_VIEW, {
						id: turbine.location,
					}),
				);
			};

			return {
				key: turbine.id,
				row_number: index + 1,
				serial_number: turbine.id ? (
					<Typography.Link onClick={onView}>
						{turbine.serial_number}
					</Typography.Link>
				) : (
					turbine.serial_number
				),
				park_number: turbine.park_number ?? <NoData intl={intl} />,
				client:
					turbine.client && clientName ? (
						<Typography.Link onClick={onViewClient}>
							{clientName}
						</Typography.Link>
					) : (
						(clientName ?? "—")
					),
				location:
					turbine.location && siteName ? (
						<Typography.Link onClick={onViewSite}>{siteName}</Typography.Link>
					) : (
						(siteName ?? "—")
					),
				updated_at: turbine.updated_at ? (
					commonColumns.dateValue(turbine.updated_at, intl)
				) : (
					<NoData intl={intl} />
				),
				actions: commonColumns.actionsValue(
					undefined,
					undefined,
					turbine,
					user,
					onView,
					onEdit,
					onDelete,
					undefined,
					"Turbine",
				),
			};
		},
	);
};

type TurbinesFilterTitleProps = {
	dispatch: Dispatch;
	currentRoute: RouterState;
	form: FormInstance;
	intl: IntlShape<string>;
	clientItems?: Client[];
	locationItems?: Location[];
};

const FacilityFilterTitle = (props: TurbinesFilterTitleProps) => {
	const { form, dispatch, currentRoute, intl, clientItems, locationItems } = props;

	return (
		<ListActionBar
			intl={intl}
			form={form}
			onFilter={onFilter(dispatch, form, currentRoute)}
			onCancel={onCancel(dispatch, form, currentRoute)}
			filterTemplate={turbineFilter}
			currentFilter={currentRoute.params}
			searchBarPlaceholderKey={intl.formatMessage({
				id: "turbine_table_header_search_placeholder",
			})}
		>
			<DrawerItemWrapper>
				<ClientSelectorFormItem intl={intl} clientItems={clientItems} />
			</DrawerItemWrapper>
			<DrawerItemWrapper>
				<LocationSelectorFormItem
					intl={intl}
					locationItems={locationItems}
					selectProps={{ allowClear: true }}
				/>
			</DrawerItemWrapper>
		</ListActionBar>
	);
};

type TurbinesPageProps = ReturnType<typeof withTurbines> & DispatchProp;
export const TurbinesPage = (props: TurbinesPageProps) => {
	const {
		results,
		dispatch,
		currentRoute,
		intl,
		user,
		turbineSideload,
		clientItems,
		locationItems,
		modalAPI,
	} = props;

	const rowData: rowDataType = {
		user,
		results,
		turbineSideload,
	};

	const pagination = results.pagination;

	const [form] = Form.useForm();

	if (!currentRoute) {
		return <Skeleton active />;
	}

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "turbine_table_header_name" })}
				subtitle={intl.formatMessage(
					{ id: "turbine_table_header_number_of_items" },
					{ no: results.pagination.total_items },
				)}
				extra={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() =>
							dispatch(routerActions.navigateTo(routeNames.TURBINE_CREATE))
						}
					>
						{intl.formatMessage({ id: "turbine_create_button" })}
					</Button>
				}
			>
				<Card
					title={
						<FacilityFilterTitle
							intl={intl}
							form={form}
							dispatch={dispatch}
							currentRoute={currentRoute}
							clientItems={clientItems}
							locationItems={locationItems}
						/>
					}
				>
					<TableWrapper
						data={rowData}
						getRows={getTableRows}
						getColumns={getColumns}
						intl={intl}
						modalApi={modalAPI}
						route={currentRoute}
						applyFilterAction={(filterParams) =>
							routerActions.navigateTo(routeNames.TURBINE_LIST, filterParams)
						}
						pagination={pagination as PaginationAndFilter}
					/>
				</Card>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
};

const withTurbines = (state: RootState) => ({
	results: state.turbine.list as PaginatedturbineResultsSideloadList,
	turbineSideload: state.turbine.sideload,
	currentRoute: state.router.route,
	intl: state.intl.datetimeIntl,
	modalAPI: state.appFeedback.modal,
	user: state.userstate.user,
	clientItems: state.ticketData.clients,
	locationItems: state.ticketData.locations,
});
export const ConnectedTurbinesPage = connect(withTurbines)(TurbinesPage);

export default ConnectedTurbinesPage;
